import { useEffect, useMemo, useState } from 'react';
import { formatDateTime, formatInOrgTz } from '../lib/datetime';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { bookingsService, dashboardService } from '../lib/services';
import { useDirectoryNames } from '../lib/useDirectoryNames';

/* ────────────────────────────────────────────────────────────
   Service Advisor — live dashboard
   ──────────────────────────────────────────────────────────── */

const STATUS_COLOR = {
  PENDING:   '',
  CONFIRMED: 'success',
  IN_SERVICE:'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
  NO_SHOW:   'danger',
};

const JOURNEY_STEPS = [
  { key: 'BOOKED',              label: 'Booked' },
  { key: 'REMINDER_SENT',       label: 'Reminder' },
  { key: 'VEHICLE_ARRIVED',     label: 'Arrived' },
  { key: 'INSPECTION_COMPLETED',label: 'Inspected' },
  { key: 'ADDITIONAL_WORK_PROPOSED', label: 'Approval call' },
  { key: 'JOB_COMPLETED',       label: 'Job done' },
  { key: 'INVOICE_SENT',        label: 'Invoice' },
  { key: 'CSI_FOLLOWUP',        label: 'CSI' },
];

export default function ServiceAdvisor() {
  const [snap, setSnap] = useState(null);
  const [today, setToday] = useState([]);
  const [selected, setSelected] = useState(null);
  const [journey, setJourney] = useState([]);
  const names = useDirectoryNames();

  // KPI snapshot
  useEffect(() => {
    let cancelled = false;
    dashboardService.snapshot({ days: 7 })
      .then((s) => { if (!cancelled) setSnap(s); })
      .catch(() => { if (!cancelled) setSnap(null); });
    return () => { cancelled = true; };
  }, []);

  // Today's bookings — also auto-refresh every 60 s.
  useEffect(() => {
    let cancelled = false;
    const load = () => bookingsService.today()
      .then((rows) => {
        if (cancelled) return;
        const arr = Array.isArray(rows) ? rows : [];
        setToday(arr);
        // Auto-select the first row so the journey panel has something to show.
        if (arr.length && !selected) setSelected(arr[0]);
      })
      .catch(() => setToday([]));
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replay journey events for the selected booking.
  useEffect(() => {
    if (!selected?.id) { setJourney([]); return; }
    let cancelled = false;
    bookingsService.journey(selected.id)
      .then((rows) => {
        if (cancelled) return;
        setJourney(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setJourney([]));
    return () => { cancelled = true; };
  }, [selected?.id]);

  const totalBookings = snap?.totalBookings ?? today.length;
  const byStatus = snap?.bookingsByStatus || {};
  const confirmed = byStatus.CONFIRMED || 0;
  const inService = byStatus.IN_SERVICE || 0;
  const completed = byStatus.COMPLETED || 0;
  const cancelled = (byStatus.CANCELLED || 0) + (byStatus.NO_SHOW || 0);
  const confirmedPct = totalBookings > 0 ? Math.round((confirmed / totalBookings) * 100) : 0;

  const kpis = [
    { l: 'TOTAL BOOKINGS', v: `${totalBookings}`, sub: 'all time', c: 'var(--accent-primary)' },
    { l: 'TODAY',          v: `${today.length}`, sub: `${snap?.bookingsInWindow ?? 0} this week`, c: 'var(--accent-primary)' },
    { l: 'CONFIRMED',      v: `${confirmed}`,    sub: `${confirmedPct}% of total`, c: 'var(--success)' },
    { l: 'IN SERVICE',     v: `${inService}`,    sub: 'active right now', c: 'var(--warning)' },
    { l: 'COMPLETED',      v: `${completed}`,    sub: 'closed RO', c: 'var(--success)' },
    { l: 'CANCELLED',      v: `${cancelled}`,    sub: 'incl. no-shows', c: 'var(--danger)' },
  ];

  const eventsByType = useMemo(() => {
    const m = new Map();
    for (const e of journey) m.set(String(e.event), e);
    return m;
  }, [journey]);

  return (
    <>
      <TopBar crumbs={['Service Advisor', 'Live']} right={
        <>
          <button className="btn small">Voice persona</button>
          <button className="btn small">Workshop diary</button>
        </>
      } />
      <div className="app-content">
        {/* KPI row */}
        <div className="grid-6" style={{ marginBottom: 16 }}>
          {kpis.map((k, i) => (
            <div key={i} className="card animate-in" style={{ animationDelay: `${i * 0.04}s` }}>
              <div className="card-label">{k.l}</div>
              <div className="metric-value" style={{ fontSize: 26, color: k.c }}>{k.v}</div>
              <div className="mono text-xs text-muted">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Workshop today + selected detail */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14, marginBottom: 16 }}>
          <div className="card animate-in" style={{ animationDelay: '0.2s' }}>
            <div className="card-label">TODAY · WORKSHOP</div>
            {today.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px 8px' }}>
                <div className="icon">🛠</div>
                <div className="title">No bookings today</div>
                <div className="desc">Bookings created via the AI agent or admin portal will show up here once their start time falls within today.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {today.map((b) => {
                  const start = b.startAt ? new Date(b.startAt) : null;
                  const tStr  = formatInOrgTz(start, { hour: '2-digit', minute: '2-digit' });
                  const isSelected = selected?.id === b.id;
                  return (
                    <div
                      key={b.id}
                      onClick={() => setSelected(b)}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: isSelected ? 'var(--accent-glow)' : 'transparent',
                        border: '1px solid ' + (isSelected ? 'var(--accent-primary)' : 'var(--border-subtle)'),
                        alignItems: 'center',
                      }}
                    >
                      <span className="mono text-xs" style={{ width: 56, fontWeight: 600 }}>{tStr}</span>
                      <div style={{ flex: 1, fontSize: 13 }}>
                        <div style={{ fontWeight: 500 }}>
                          {names.customer(b.customerId, b.customerName)}
                        </div>
                        <div className="mono text-xs text-muted">
                          {b.type || 'Booking'}
                          {(b.vehicleId || b.vehicleLabel) ? ` · ${names.vehicle(b.vehicleId, b.vehicleLabel)}` : ''}
                        </div>
                      </div>
                      <span className={`pill ${STATUS_COLOR[b.status] || ''} ${STATUS_COLOR[b.status] ? 'filled' : ''}`}>
                        {b.status || 'PENDING'}
                      </span>
                      {b.customerId && (
                        <Link
                          to={`/customers/${b.customerId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="mono text-xs"
                          style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}
                        >open</Link>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card animate-in" style={{ animationDelay: '0.25s' }}>
            <div className="card-label">SELECTED · DETAIL</div>
            {!selected ? (
              <div className="empty-state" style={{ padding: '24px 8px' }}>
                <div className="title">Pick a booking</div>
                <div className="desc">Click any row on the left to see status, customer, and the on-the-day journey.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                <Row k="Type"        v={selected.type} />
                <Row k="Status"      v={<span className={`pill ${STATUS_COLOR[selected.status] || ''} ${STATUS_COLOR[selected.status] ? 'filled' : ''}`}>{selected.status}</span>} />
                <Row k="Starts"      v={formatDateTime(selected.startAt)} />
                <Row k="Ends"        v={formatDateTime(selected.endAt)} />
                <Row k="Vehicle"     v={(selected.vehicleId || selected.vehicleLabel) ? names.vehicle(selected.vehicleId, selected.vehicleLabel) : '—'} />
                <Row k="Customer"    v={selected.customerId
                    ? <Link to={`/customers/${selected.customerId}`} style={{ color: 'var(--accent-primary-hover)' }}>{names.customer(selected.customerId, selected.customerName)}</Link>
                    : '—'} />
                <Row k="Lead"        v={selected.leadId ? <Link to={`/leads/${selected.leadId}`} style={{ color: 'var(--accent-primary-hover)' }}>{names.lead(selected.leadId, selected.leadName, { showId: true })}</Link> : '—'} />
                <Row k="Sync status" v={selected.dmsWritebackStatus || '—'} />
              </div>
            )}
          </div>
        </div>

        {/* On-the-day journey for the selected booking */}
        <div className="card animate-in" style={{ animationDelay: '0.3s' }}>
          <div className="card-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>ON-THE-DAY JOURNEY</span>
            {selected && (
              <span className="mono text-xs text-muted" style={{ marginLeft: 'auto' }}>
                {journey.length} event{journey.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {!selected ? (
            <div className="mono text-xs text-muted" style={{ padding: 12 }}>
              Pick a booking above to render its journey.
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, padding: '12px 0', overflowX: 'auto' }}>
              {JOURNEY_STEPS.map((step, i) => {
                const ev = eventsByType.get(step.key);
                const done = !!ev;
                const isCurrent = !done && i === firstUndoneIndex(JOURNEY_STEPS, eventsByType);
                return (
                  <div key={step.key} style={{ display: 'contents' }}>
                    <div className="journey-step">
                      <div className={`journey-dot ${done ? 'done' : isCurrent ? 'now' : ''}`}>
                        {done ? '✓' : isCurrent ? '…' : i + 1}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{step.label}</div>
                      <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
                        {formatInOrgTz(ev?.occurredAt, { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    {i < JOURNEY_STEPS.length - 1 && (
                      <div style={{
                        flex: '0 0 24px', height: 2, marginTop: 14, borderRadius: 1,
                        background: done ? 'var(--accent-primary)' : 'var(--border-subtle)',
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex-between" style={{ padding: '4px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <span className="text-muted">{k}</span>
      <span>{v ?? '—'}</span>
    </div>
  );
}

function firstUndoneIndex(steps, eventsByType) {
  for (let i = 0; i < steps.length; i++) {
    if (!eventsByType.has(steps[i].key)) return i;
  }
  return -1;
}
